/**
 * Where the first-run wizard's dismissal lives, and why it lives there.
 *
 * The key shape `firstRunWizard.dismissed.{workspaceId}` is transferred verbatim from kms. It
 * is per-workspace and per-browser, and CHANGING it would re-show the wizard to everybody who
 * had already dismissed it — which is the whole reason a shape this specific is worth pinning
 * in its own module rather than inlining a template string at two call sites.
 *
 * Every read and write is wrapped: a private window, cleared site data or a browser set to
 * block storage all throw on access, and none of them is a reason to fail somebody's first
 * five minutes in the product.
 */
export function dismissKeyFor(workspaceId: string): string {
  return `firstRunWizard.dismissed.${workspaceId}`;
}

export function isWizardDismissed(workspaceId: string | undefined): boolean {
  if (!workspaceId) {
    return false;
  }
  try {
    return window.localStorage.getItem(dismissKeyFor(workspaceId)) === "true";
  } catch {
    return false;
  }
}

export function dismissWizard(workspaceId: string | undefined): void {
  if (!workspaceId) {
    return;
  }
  try {
    window.localStorage.setItem(dismissKeyFor(workspaceId), "true");
  } catch {
    // Storage unavailable. The wizard simply shows again next time, which is the safe half.
  }
}

/**
 * Should the wizard take over?
 *
 * `hasOnlyWizardOwnCollection` is the **step-aside exemption** and it is easy to leave out:
 * creating the collection in step 1 must not, one render later, satisfy "this workspace has a
 * collection" and evict the wizard at step 2. So the collection the wizard just made does not
 * count against it.
 *
 * `capable !== false` rather than `capable === true`: while `wizard.capabilities` is still in
 * flight the answer is undefined, and a wizard that flickers away and back is worse than one
 * that waits.
 */
export function shouldShowWizard(input: {
  capable: boolean | undefined;
  collectionsLoaded: boolean;
  activeCollectionIds: string[];
  createdCollectionId: string | null;
  dismissed: boolean;
  finished: boolean;
}): boolean {
  if (input.capable === false || !input.collectionsLoaded || input.dismissed || input.finished) {
    return false;
  }
  if (input.activeCollectionIds.length === 0) {
    return true;
  }
  return (
    input.activeCollectionIds.length === 1 &&
    input.createdCollectionId !== null &&
    input.activeCollectionIds[0] === input.createdCollectionId
  );
}
